import { useState } from 'react'
import { X, Send, Plus, Check } from 'lucide-react'
import { CATEGORIES } from '../db/database'
import { Icon } from '../ui/icons'
import useAuthStore from '../store/authStore'
import useTaskStore from '../store/taskStore'
import { useT } from '../i18n/useT'

const TASK_SHARE_CAP = 2  // max recipient attivi per task (sender + 2 = 3 totali)
const ACTIVE_SHARE_STATUSES = ['pending', 'accepted', 'completed']

export default function ShareModal({ task, onClose }) {
  const t    = useT()
  const user = useAuthStore(s => s.user)
  const outboundShares = useTaskStore(s => s.outboundShares)

  const [emails, setEmails]     = useState([])
  const [input, setInput]       = useState('')
  const [sending, setSending]   = useState(false)
  const [error, setError]       = useState('')
  const [done, setDone]         = useState(false)
  const [results, setResults]   = useState([])

  const cat = CATEGORIES[task.category] || CATEGORIES.casa

  // Le attività ricevute non si possono ri-condividere (no catene).
  const isReceived = !!task.sharedOriginId

  // Slot già occupati su questo task — count locale dei share "attivi".
  // Usiamo la mappa già popolata dal taskStore (Realtime mantiene up-to-date).
  const activeShares = (outboundShares[task.id] ?? []).filter(
    s => ACTIVE_SHARE_STATUSES.includes(s.status)
  )
  const slotsUsed = activeShares.length
  const slotsRemaining = Math.max(0, TASK_SHARE_CAP - slotsUsed)
  const canAddMore = slotsRemaining > emails.length

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)

  const addEmail = () => {
    const email = input.trim().toLowerCase()
    if (!email) return
    if (!validateEmail(email)) { setError(t('share_err_email')); return }
    if (email === user?.email?.toLowerCase()) { setError(t('share_err_self')); return }
    if (emails.includes(email)) { setError(t('share_err_duplicate')); return }
    if (activeShares.some(s => s.recipientEmail === email)) {
      setError(t('share_err_already_shared')); return
    }
    if (!canAddMore) { setError(t('share_err_task_cap', { max: TASK_SHARE_CAP })); return }
    setEmails(prev => [...prev, email])
    setInput('')
    setError('')
  }

  const removeEmail = (i) => setEmails(prev => prev.filter((_, idx) => idx !== i))

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addEmail()
    }
  }

  const handleSend = async () => {
    if (!emails.length || sending) return
    setSending(true)
    setError('')
    try {
      const token = useAuthStore.getState().session?.access_token
      if (!token) throw new Error('auth')

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/share-task`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            recipients: emails,
            senderTaskId: task.id || null,
            taskSnapshot: {
              title:           task.title,
              category:        task.category,
              dueDate:         task.dueDate,
              dueTime:         task.dueTime || null,
              priority:        task.priority,
              notes:           task.notes || null,
              reminder:        task.reminder || false,
              reminderMinutes: task.reminderMinutes ?? null,
              durationMinutes: task.durationMinutes ?? null,
            },
          }),
        }
      )
      const json = await res.json()

      if (res.status === 403 && json.error === 'no_reshare') {
        setError(t('share_err_no_reshare')); setSending(false); return
      }
      if (res.status === 429 && json.error === 'task_share_cap') {
        setError(t('share_err_task_cap', { max: TASK_SHARE_CAP })); setSending(false); return
      }
      if (res.status === 429) { setError(t('share_err_limit')); setSending(false); return }
      if (!res.ok) throw new Error(json.error || 'server')

      setResults(json.results || [])
      setDone(true)
    } catch (e) {
      console.error('[Share]', e)
      setError(t('share_err_generic'))
    } finally {
      setSending(false)
    }
  }

  if (done) {
    return (
      <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
        <div className="modal-sheet" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
          <div className="modal-handle" />
          <div style={{ padding: '32px 20px', textAlign: 'center' }}>
            <Icon.Success size={48} color="#27AE60" style={{ marginBottom: 12 }} />
            <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>{t('share_success')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '16px 0' }}>
              {results.map((r, i) => {
                const Channel = r.channel === 'push' ? Icon.Bell : Icon.Mail
                const label = r.channel === 'push' ? 'Push' : r.channel === 'email_invite' ? t('share_channel_invite') : 'Email'
                return (
                  <div key={i} style={{ fontSize: '0.82rem', color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                    <Check size={14} color="var(--primary)" />
                    {r.email} —
                    <Channel size={13} />
                    {label}
                  </div>
                )
              })}
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={onClose}>
              {t('share_close')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Task ricevuto via share → impossibile ri-condividere (no catene).
  if (isReceived) {
    return (
      <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
        <div className="modal-sheet" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
          <div className="modal-handle" />
          <div className="modal-header">
            <span className="modal-title">{t('share_title')}</span>
            <button className="btn-icon" onClick={onClose}><X size={20} /></button>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '24px 20px' }}>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-2)', marginBottom: 16 }}>
              {t('share_err_no_reshare')}
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={onClose}>
              {t('share_close')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-sheet" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        <div className="modal-header">
          <span className="modal-title">{t('share_title')}</span>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-body">
          {/* Task preview */}
          <div className="share-task-preview">
            <span style={{ color: cat.color, display: 'inline-flex' }}>
              <cat.Icon size={18} strokeWidth={2} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{task.title}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-2)', marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Icon.Calendar size={12} />
                {task.dueDate}{task.dueTime ? ` · ${task.dueTime}` : ''}
              </div>
            </div>
          </div>

          <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', margin: '0 0 8px' }}>
            {t('share_sub')}
          </p>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-3)', margin: '0 0 12px' }}>
            {t('share_task_slots', { used: slotsUsed, max: TASK_SHARE_CAP })}
          </p>

          {/* Email chips */}
          {emails.length > 0 && (
            <div className="share-chips">
              {emails.map((email, i) => (
                <span key={i} className="share-chip">
                  {email}
                  <button onClick={() => removeEmail(i)}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}

          {/* Email input */}
          <div className="share-input-row">
            <input
              type="email"
              value={input}
              onChange={e => { setInput(e.target.value); setError('') }}
              onKeyDown={handleKeyDown}
              placeholder={t('share_email_ph')}
              autoComplete="email"
              disabled={!canAddMore}
            />
            <button className="btn btn-ghost" onClick={addEmail} disabled={!input.trim() || !canAddMore}>
              <Plus size={16} /> {t('share_add_email')}
            </button>
          </div>

          {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}

          <button
            className="btn btn-primary"
            style={{ width: '100%', marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onClick={handleSend}
            disabled={!emails.length || sending}
          >
            <Send size={16} />
            {sending ? t('share_sending') : t('share_send')}
          </button>
        </div>
      </div>
    </div>
  )
}
